import { useId, useState, type FormEvent } from "react";
import { useResetUserPassword } from "../../../api/queries";
import type { User } from "../../../api/types";
import { Button } from "../../../components/Button";
import { Modal } from "../../../components/Modal";
import { useToast } from "../../../toast/ToastContext";
import "../../../components/Form.css";

interface ResetPasswordModalProps {
  user: User | null;
  onClose: () => void;
}

/**
 * An admin setting someone else's password (distinct from the self-service
 * change-password form on the account page). The confirm field is a
 * client-side-only safety check — it never leaves this component; only
 * `password` is sent, and only once (S-13's spirit: nothing here holds a
 * password a moment longer than the request needs).
 */
export function ResetPasswordModal({ user, onClose }: ResetPasswordModalProps) {
  const passwordId = useId();
  const confirmId = useId();
  const { pushToast } = useToast();
  const mutation = useResetUserPassword();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  function reset() {
    setPassword("");
    setConfirm("");
  }

  function handleClose() {
    reset();
    mutation.reset();
    onClose();
  }

  const mismatch = confirm.length > 0 && password !== confirm;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || mismatch) return;
    const { id, username } = user;
    mutation.mutate(
      { id, password },
      {
        onSuccess: () => {
          pushToast({ title: `Password reset for "${username}".`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't reset the password for "${username}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    reset();
  }

  return (
    <Modal
      open={user !== null}
      title={user ? `Reset password for "${user.username}"` : "Reset password"}
      onClose={handleClose}
      initialFocusId={passwordId}
    >
      <form onSubmit={handleSubmit} noValidate>
        <p className="form-field__hint">
          Sets their password directly — they won't get a chance to pick their own. Tell them the new password out
          of band; it isn't shown here again.
        </p>

        <label className="form-field" htmlFor={passwordId}>
          <span>New password</span>
          <input
            id={passwordId}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
          <span className="form-field__hint">At least 8 characters.</span>
        </label>

        <label className="form-field" htmlFor={confirmId}>
          <span>Confirm new password</span>
          <input
            id={confirmId}
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          {mismatch && (
            <span className="form-field__error" role="alert">
              Passwords don't match.
            </span>
          )}
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || password.length < 8 || mismatch}>
            {mutation.isPending ? "Resetting…" : "Reset password"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
